<!-- src/features/imprint/ImprintNavigationController.vue -->
<template>
    <div class="imprint-navigation">
        <!-- Product Info Section - Always Open -->
        <section class="imprint-navigation__product" id="pricing-section">
            <ImprintPriceMatrix/>
            
            <!-- Retail Value -->
            <div v-if="retailValue" class="retail-value">
                <span class="retail-value__tooltip-wrap">
                    <span class="retail-value__tooltip-icon">?</span>
                    <span class="retail-value__tooltip-content">
                        <b>Retail Value</b>
                        The cost to buy one of these products, fully customized, at a retail store.
                    </span>
                </span>
                <span class="retail-value__label">Retail Value:</span>
                <span class="retail-value__price">${{ retailValue.toFixed(2) }}</span>
            </div>
            
            <!-- Normal product actions -->
            <div v-if="appStore.applicationType !== 'calendar_single'" class="product-actions">
                <BaseButton
                    text="Request a Sample"
                    variant="outline-yellow"
                    size="medium"
                    @click="handleRequestSample"
                />
                <BaseButton
                    text="Begin Customizing"
                    variant="primary"
                    size="medium"
                    @click="handleBeginCustomizing"
                />
            </div>
            
            <!-- Calendar Single Actions -->
            <div v-else>
                <CalendarSinglePage />
            </div>
        </section>
        
        <!-- Normal product flow continues -->
        <template v-if="appStore.applicationType !== 'calendar_single'">

            <!-- Calendar Products Section (only for calendar modes) -->
            <section
                style="display: none"
                v-if="appStore.isCalendarMode" 
                class="calendar-products-section"
            >
                <CalendarProductList />
            </section>

            <!-- Collapsible Sections -->
            <div v-if="sections.length > 0" class="imprint-navigation__sections">
            <div
                v-for="section in sections"
                :key="section.id"
                :data-section-id="section.id"
                class="imprint-navigation__section"
                :class="{ 
                  'is-open': isSectionOpen(section.id),
                  'is-disabled': !canOpenSection(section.id),
                  'is-hidden': shouldHideSection(section.id),
                  'is-shaking': shakingSections.includes(section.id)
                }"
            >
                <!-- Section Header (Toggle Button) -->
                <div class="section-header-wrap">
                <button
                    type="button"
                    class="imprint-navigation__section-header"
                    :class="{ 'is-disabled': !canOpenSection(section.id) }"
                    :disabled="!canOpenSection(section.id)"
                    @click="onSectionClick(section.id)"
                >
                    <div class="section-header-left">
                        <span class="section-title">
                            {{ section.label }}
                        </span>
                    </div>

                    <!-- Error indicator with tooltip -->
                    <div 
                        v-if="getSectionErrorCount(section.id) > 0 && section.id != 'review' && isSectionValidated(section.id)"
                        class="section-errors-wrapper"
                    >
                        <div class="section-errors">
                            <svg class="error-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                            </svg>
                            <span class="error-count">{{ getSectionErrorCount(section.id) }} {{ getSectionErrorCount(section.id) === 1 ? 'issue' : 'issues' }}</span>
                        </div>
                        
                        <!-- Tooltip with error list -->
                        <div class="error-tooltip">
                            <div class="error-tooltip-header">
                                Please fix the following {{ getSectionErrorCount(section.id) }} {{ getSectionErrorCount(section.id) === 1 ? 'issue' : 'issues' }}:
                            </div>
                            <ul class="error-tooltip-list">
                                <li v-for="(error, index) in getSectionErrors(section.id)" :key="index">
                                    {{ error }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div v-else-if="section.meta" class="section-meta">
                        {{ section.meta }}
                    </div>
                </button>
                </div>
                <!-- Section Content Panel -->
                <transition name="slide-fade">
                    <!-- TODO check diff v-if v-show -->
                    <section
                        v-show="isSectionOpen(section.id)"
                        class="imprint-navigation__panel"
                    >
                        <!-- Colour & Quantity -->
                        <template v-if="section.id === 'colourQuantity'">
                            <ColorSizeSection
                                v-if="currentImprintKey && currentImprintKey !== 'default-imprint'"
                                ref="colorSizeSection"
                                :imprint-key="currentImprintKey"
                                :description = "section.description"
                                :reset-trigger="colorSectionResetTrigger"
                                @next-step="handleColorQuantityComplete"
                            />
                            <div v-else class="loading-state">
                                <p>Loading product data...</p>
                            </div>
                        </template>

                        <!-- Customize Product (Step 2) -->
                        <template v-else-if="section.id === 'imprintSelection'">
                            <CustomizeProductSection
                                :product-data="fullProductData"
                                @method-selected="handleMethodSelected"
                                @previous-step="handleCustomizePreviousStep"
                                @next-step="handleCustomizeNextStep"
                                @design-online-returned="handleDesignOnlineReturned"
                            />
                        </template>

                        <!-- Customize Product -->
                        <template v-else-if="section.id === 'customize'">
                            <CustomizeSection
                                @location-changed="handleLocationChange"
                                @artwork-uploaded="handleArtworkUpload"
                            />
                        </template>

                        <!-- Add-Ons -->
                        <template v-else-if="section.id === 'addons'">
                            <AddOnsSection
                                @addons-changed="handleAddonsChange"
                            />
                        </template>

                        <!-- Review & Add to Cart -->
                        <template v-else-if="section.id === 'review'">
                            <ReviewSection
                                @go-back="handleGoBack"
                                @add-to-cart="handleAddToCart"
                                @validation-failed="handleValidationFailed"
                                @scroll-to-section="handleScrollToSection"
                                @scroll-to-field="handleScrollToField"
                            />
                        </template>
                    </section>
                </transition>
            </div>
        </div>
        </template>
    </div>
</template>

<script>
import { useImprintStore } from './stores/useImprintStore.js';
import { useSelectionStore } from './stores/useSelectionStore.js';
import { useAppStore } from './stores/useAppStore.js';
import { useCalendarStore } from './stores/useCalendarStore.js';
import { useAddToCartStore } from './stores/useAddToCart.js';
import { useNewImprintStore } from './stores/useNewImprintStore.js';
import ImprintPriceMatrix from "@/features/imprint/components/ImprintPriceMatrix.vue";
import BaseButton from '@/features/imprint/components/BaseButton.vue';
import CalendarProductList from '@/features/imprint/components/CalendarProductList.vue';
import CalendarSinglePage from '@/features/calendar/CalendarSinglePage.vue';
import ColorSizeSection from '@/features/imprint/components/ColorSizeSection.vue';
import CustomizeProductSection from '@/features/imprint/sections/CustomizeProductSection.vue';
import CustomizeSection from '@/features/imprint/sections/CustomizeSection.vue';
import AddOnsSection from '@/features/imprint/sections/AddOnsSection.vue';
import ReviewSection from '@/features/imprint/sections/ReviewSection.vue';
import { mockProduct } from '@/../public/mock-data/products';
import { useMockData } from '@/../public/mock-data/environment';
import { useSectionValidation } from './composables/useSectionValidation';
import { useDataCleanup } from './composables/useDataCleanup';  // ✅ NEW: For clearing data after Add to Cart
import { DEBUG_CONFIG } from './config/debug.js';

export default {
    name: 'ImprintNavigationController',

    components: {
        ImprintPriceMatrix,
        BaseButton,
        CalendarProductList,
        CalendarSinglePage,
        ColorSizeSection,
        CustomizeProductSection,
        CustomizeSection,
        AddOnsSection,
        ReviewSection
    },

    setup() {
        const sectionValidation = useSectionValidation();
        
        return {
            sectionValidation,
            debugConfig: DEBUG_CONFIG
        };
    },

    data() {
        return {
            // Tracks sections where Next Step was clicked (to show errors only after attempt)
            validatedSections: new Set(),
            // When true, show errors in ALL sections (triggered by opening Review tab or Add to Cart click)
            showAllSectionErrors: false,
            // Section IDs currently playing shake animation
            shakingSections: [],
            colorSectionResetTrigger: 0,
        }
    },

    computed: {
        imprintStore() {
            return useImprintStore();
        },

        selectionStore() {
            return useSelectionStore();
        },

        appStore(){
            return  useAppStore();
        },

        addToCartStore() {
            return useAddToCartStore();
        },
        
        calendarStore() {
            return useCalendarStore();
        },

        sections() {
            // Return sections from store (reactive state)
            // Store sections are initialized based on application type in mounted()
            return this.imprintStore.sections;
        },
        
        fullProductData() {
            return this.imprintStore.fullJson || {};
        },

        currentImprintKey() {
            // Get current imprint method SKU from store
            return this.imprintStore.getImprintMethodKey || 'default-imprint';
        },
        
        /**
         * Get retail value (tierMSRP) from prices_2 for current imprint method
         * @returns {number|null}
         */
        retailValue() {
            const selectedMethod = this.imprintStore.selectedImprintMethod;
            if (!selectedMethod || !selectedMethod.sku) return null;
            
            const methodData = this.imprintStore.configurableArray[selectedMethod.sku];
            if (!methodData || !methodData.prices_2) return null;
            
            // Get MSRP from prices_2
            const msrp = methodData.prices_2.msrp;
            return msrp ? Number(msrp) : null;
        }
    },

    methods: {
        /**
         * Trigger field-level validation for all imprints (artworkMethod === 'new').
         * Fills imprint.errors so has-error CSS class appears on fields
         * in StartNewImprintForm_Pinia.vue at the same moment error badges become visible.
         */
        triggerFieldLevelValidation() {
            if (this.imprintStore.selectedArtworkMethod === 'new') {
                const newImprintStore = useNewImprintStore();
                newImprintStore.imprints.forEach((_, index) => {
                    newImprintStore.validate(index);
                });
            }
        },

        /**
         * Load mock data for development
         */
        loadMockDataFullJson() {
            try {
                const fullJson = mockProduct.full_json
                this.imprintStore.setFullJson(JSON.parse(fullJson))
                this.$log('[MOCK]-----> Start Full Json // ImprintNavigationController.vue')
            } catch (e) {
                this.$log('[MOCK]-----> Not set')
                this.imprintStore.setFullJson(null)
            }
        },

        initFullJson() {
            if (this.imprintStore.fullJson) {
                console.log('[ImprintNavigationController] fullJson already loaded');
                return;
            }

            // For calendar mode, try to load from appStore.full_product first
            if (this.appStore.isCalendarMode && this.appStore.full_product) {
                console.log('[ImprintNavigationController] Loading fullJson from appStore.full_product (calendar mode)');
                this.imprintStore.setFullJson(this.appStore.full_product);
                
                this.$log('-----> Start Full Json (Calendar) // ImprintNavigationController.vue');
                console.warn('-----> Start Full Json (Calendar) // ImprintNavigationController.vue');
                console.log(this.appStore.full_product);
                console.warn('-----> End Full Json');
                return;
            }

            if (!useMockData()) {
                const el = document.getElementById('full_product_info_json');
                if (!el) {
                    console.warn('[Imprint] full_product_info_json element not found');
                    this.imprintStore.setFullJson(null);
                    return;
                }

                try {
                    const raw = (el.textContent || '').trim();
                    const fullJson = raw ? JSON.parse(raw) : null;

                    this.imprintStore.setFullJson(fullJson);

                    this.$log('-----> Start Full Json // ImprintNavigationController.vue');
                    console.warn('-----> Start Full Json // ImprintNavigationController.vue');
                    console.log(fullJson);
                    console.warn('-----> End Full Json');
                } catch (e) {
                    console.warn('[Imprint] Failed to parse full_product_info_json:', e);
                    this.imprintStore.setFullJson(null);
                }
            } else {
                this.loadMockDataFullJson();
            }
        },

        onSectionClick(id) {
            if (this.canOpenSection(id)) {
                this.imprintStore.toggleSectionOpen(id);

                // Opening Review tab: show error badges on ALL sections + field-level highlights
                if (id === 'review') {
                    this.showAllSectionErrors = true;
                    this.triggerFieldLevelValidation();
                }
            }
        },

        isSectionOpen(id) {
            return this.imprintStore.isSectionOpen(id);
        },

        canOpenSection(id) {
            return this.imprintStore.canOpenSection(id);
        },

        /**
         * Check if section should be hidden
         * @param {string} id - Section ID
         * @returns {boolean}
         */
        shouldHideSection(id) {
            // Hide Color & Quantity section for calendar modes
            if (id === 'colourQuantity' && this.appStore.isCalendarMode) {
                return true;
            }
            
            // Hide Add-Ons section if debug config is false
            if (id === 'addons' && !this.debugConfig.showAddOnsSection) {
                return true;
            }
            
            return false;
        },

        // Event Handlers
        handleRequestSample() {
            console.log('Request sample clicked');
            // TODO: Open sample request modal or redirect
            // For now, just log the action
        },

        handleBeginCustomizing() {
            console.log('[ImprintNavigationController] Begin customizing clicked');
            
            // Determine first section based on application type
            let firstSectionId = 'colourQuantity';
            
            // For calendar mode, first section is imprintSelection (Customize Your Product)
            if (this.appStore.isCalendarMode) {
                firstSectionId = 'imprintSelection';
                console.log('[ImprintNavigationController] Calendar mode - opening imprintSelection');
            }
            
            // If colourQuantity is already open, reset the expanded color selection
            // so activeColorId falls back to defaultActiveColorId (client requirements)
            if (firstSectionId === 'colourQuantity') {
                this.colorSectionResetTrigger++
            }

            // Open first section if not already open
            if (!this.isSectionOpen(firstSectionId)) {
                this.imprintStore.toggleSectionOpen(firstSectionId);
            }
            
            // Scroll to the first section with improved safety logic
            this.$nextTick(() => {
                setTimeout(() => {
                    const firstSection = document.querySelector(`[data-section-id="${firstSectionId}"]`);
                    if (firstSection) {
                        const currentScroll = window.pageYOffset;
                        const rect = firstSection.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80;

                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);

                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 500);
            });
        },

        handleMethodSelected(method) {
            console.log('Imprint method selected:', method);
            // Store selected method
            // Unlock next section when method is selected
            this.imprintStore.unlockSection('addons');
        },

        /**
         * Handle Design Online return from Studio
         * Open Step 2 and scroll to it
         */
        handleDesignOnlineReturned() {
            console.log('[AUTO_SWITCH] 🎯 handleDesignOnlineReturned() - Opening Step 2 and scrolling');
            
            // Ensure Step 2 is unlocked
            console.log('[AUTO_SWITCH] Unlocking imprintSelection section');
            this.imprintStore.unlockSection('imprintSelection');
            
            // Close Step 1 if open
            if (this.isSectionOpen('colourQuantity')) {
                console.log('[AUTO_SWITCH] Closing colourQuantity section');
                this.imprintStore.toggleSectionOpen('colourQuantity');
            }
            
            // Open Step 2 if not already open
            if (!this.isSectionOpen('imprintSelection')) {
                console.log('[AUTO_SWITCH] Opening imprintSelection section');
                this.imprintStore.toggleSectionOpen('imprintSelection');
            } else {
                console.log('[AUTO_SWITCH] imprintSelection already open');
            }
            
            // Scroll to Step 2
            this.$nextTick(() => {
                setTimeout(() => {
                    console.log('[AUTO_SWITCH] Attempting to scroll to Step 2...');
                    const section = document.querySelector('[data-section-id="imprintSelection"]');
                    if (section) {
                        const currentScroll = window.pageYOffset;
                        const rect = section.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80; // Header offset
                        
                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);
                        
                        console.log('[AUTO_SWITCH] 📍c Scrolling to Step 2:', {
                            currentScroll,
                            sectionTop,
                            scrollToPosition
                        });
                        
                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                        
                        console.log('[AUTO_SWITCH] ✅ Scroll initiated');
                    } else {
                        console.error('[AUTO_SWITCH] ❌ Step 2 section not found!');
                    }
                }, 500); // Wait for section to open
            });
        },

        /**
         * Handle Next Step button from Colour & Quantity section
         * Close Step 1 and open Step 2 (Customize Your Product)
         */
        handleColorQuantityComplete() {
            console.log('[ImprintNavigationController] Color & Quantity complete');
            this.markSectionValidated('colourQuantity');

            // Unlock next section
            this.imprintStore.unlockSection('imprintSelection');

            // Close current section
            if (this.isSectionOpen('colourQuantity')) {
                this.imprintStore.toggleSectionOpen('colourQuantity');
            }

            // Open next section
            if (!this.isSectionOpen('imprintSelection')) {
                this.imprintStore.toggleSectionOpen('imprintSelection');
            }

            // Scroll to next section with improved safety logic
            this.$nextTick(() => {
                setTimeout(() => {
                    const section = document.querySelector('[data-section-id="imprintSelection"]');
                    if (section) {
                        const currentScroll = window.pageYOffset;
                        const rect = section.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80;

                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);

                        console.log('[ImprintNavigationController] Scrolling to Customize section:', {
                            sectionId: 'imprintSelection',
                            currentScroll,
                            sectionTop,
                            scrollToPosition
                        });

                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        console.warn('[ImprintNavigationController] Customize section not found');
                    }
                }, 500);
            });
        },
        
        /**
         * Handle Previous Step button from Customize Product section
         * Close current section and open Colour & Quantity
         */
        handleCustomizePreviousStep() {
            console.log('[ImprintNavigationController] Customize Previous Step clicked');
            
            // Reset color selection to defaultActiveColorId (client requirements)
            this.colorSectionResetTrigger++
            
            // Close current section
            if (this.isSectionOpen('imprintSelection')) {
                this.imprintStore.toggleSectionOpen('imprintSelection');
            }
            
            // Open previous section
            if (!this.isSectionOpen('colourQuantity')) {
                this.imprintStore.toggleSectionOpen('colourQuantity');
            }
            
            // Scroll to previous section with improved safety logic
            this.$nextTick(() => {
                setTimeout(() => {
                    const section = document.querySelector('[data-section-id="colourQuantity"]');
                    if (section) {
                        const currentScroll = window.pageYOffset;
                        const rect = section.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80;

                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);

                        console.log('[ImprintNavigationController] Scrolling to previous section:', {
                            sectionId: 'colourQuantity',
                            currentScroll,
                            sectionTop,
                            scrollToPosition
                        });

                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        console.warn('[ImprintNavigationController] Previous section not found');
                    }
                }, 500);
            });
        },
        
        /**
         * Handle Next Step button from Customize Product section
         * Close current section and open Add-Ons or Review (if Add-Ons hidden)
         */
        handleCustomizeNextStep() {
            console.log('[ImprintNavigationController] Customize Next Step clicked');
            this.markSectionValidated('imprintSelection');

            // Trigger field-level validation for StartNewImprint form.
            // This fills imprint.errors in the store, which causes has-error CSS class
            // to appear on text inputs and colour swatches in StartNewImprintForm_Pinia.vue.
            // Errors persist in the store so red borders are visible when user returns to Step 2.
            this.triggerFieldLevelValidation();
            
            // Close current section
            if (this.isSectionOpen('imprintSelection')) {
                this.imprintStore.toggleSectionOpen('imprintSelection');
            }
            
            // Determine next section based on config
            let nextSectionId = 'addons';

            // If Add-Ons section is hidden, skip to Review
            if (!this.debugConfig.showAddOnsSection) {
                nextSectionId = 'review';
                console.log('[ImprintNavigationController] Skipping Add-Ons section (hidden), going to Review');
            }

            // Unlock and open next section
            this.imprintStore.unlockSection(nextSectionId);
            if (!this.isSectionOpen(nextSectionId)) {
                this.imprintStore.toggleSectionOpen(nextSectionId);
            }
            
            // Scroll to next section with improved safety logic
            this.$nextTick(() => {
                // Wait for transition to complete (increased timeout)
                setTimeout(() => {
                    const section = document.querySelector(`[data-section-id="${nextSectionId}"]`);
                    if (section) {
                        // Get current scroll position
                        const currentScroll = window.pageYOffset;

                        // Get section position relative to viewport
                        const rect = section.getBoundingClientRect();

                        // Calculate absolute position
                        const sectionTop = currentScroll + rect.top;

                        // Larger offset for better visibility
                        const offset = 80;

                        // Calculate target with safety checks
                        let scrollToPosition = sectionTop - offset;

                        // Don't scroll beyond document end
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);

                        // Don't scroll before document start
                        scrollToPosition = Math.max(0, scrollToPosition);

                        console.log('[ImprintNavigationController] Scrolling to section:', {
                            sectionId: nextSectionId,
                            currentScroll,
                            sectionTop,
                            offset,
                            scrollToPosition,
                            maxScroll,
                            windowHeight: window.innerHeight,
                            documentHeight: document.documentElement.scrollHeight
                        });

                        // Smooth scroll to position
                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        console.warn('[ImprintNavigationController] Section not found:', nextSectionId);
                    }
                }, 500); // Increased timeout from 350ms to 500ms
            });
        },

        handleLocationChange(location) {
            console.log('Location changed:', location)
            // TODO: Update store with location
        },

        handleArtworkUpload(file) {
            console.log('Artwork uploaded:', file)
            // TODO: Handle artwork upload
        },

        handleAddonsChange(addons) {
            console.log('Addons changed:', addons)
            // TODO: Update store with addons
        },

        /**
         * Handle validation failure from ReviewSection.
         * Shows errors in all steps, shakes sections with errors,
         * and scrolls to the first section that needs attention.
         * @param {string[]} sectionIds - IDs of sections with errors
         */
        handleValidationFailed(sectionIds) {
            console.log('[ImprintNavigationController] Validation failed, sections:', sectionIds);

            // Show errors in ALL sections (same as opening Review)
            this.showAllSectionErrors = true;

            // Shake all sections that have errors
            this.shakingSections = [...sectionIds];
            setTimeout(() => {
                this.shakingSections = [];
            }, 700);

            // Open first section with errors and scroll to it
            if (sectionIds.length > 0) {
                this.handleScrollToSection(sectionIds[0]);
            }
        },

        /**
         * Open a section and scroll to it.
         * Used for error navigation from clickable error items.
         * @param {string} sectionId
         */
        /**
         * Open a section, scroll to it, then scroll to a specific field element.
         * Used when clicking a Step 2 error in the Review error list.
         * @param {{ sectionId: string, fieldSelector: string }}
         */
        handleScrollToField({ sectionId, fieldSelector }) {
            console.log('[ImprintNavigationController] Scrolling to field:', fieldSelector, 'in section:', sectionId);

            // Ensure section is unlocked and open
            this.imprintStore.unlockSection(sectionId);
            if (!this.isSectionOpen(sectionId)) {
                this.imprintStore.toggleSectionOpen(sectionId);
            }

            this.$nextTick(() => {
                // Wait for section to open and Vue to render the expanded content
                setTimeout(() => {
                    const el = document.querySelector(fieldSelector);
                    if (el) {
                        const currentScroll = window.pageYOffset;
                        const rect = el.getBoundingClientRect();
                        const elTop = currentScroll + rect.top;
                        // Offset: leave 140px above the element so it's clearly visible
                        const offset = 140;

                        let scrollTo = elTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollTo = Math.min(scrollTo, maxScroll);
                        scrollTo = Math.max(0, scrollTo);

                        window.scrollTo({ top: scrollTo, behavior: 'smooth' });

                        // Brief highlight flash to draw attention
                        el.classList.add('error-field-flash');
                        setTimeout(() => el.classList.remove('error-field-flash'), 1200);
                    } else {
                        console.warn('[ImprintNavigationController] Field element not found:', fieldSelector);
                        // Fall back to section scroll
                        this.handleScrollToSection(sectionId);
                    }
                }, 400);
            });
        },

        handleScrollToSection(sectionId) {
            console.log('[ImprintNavigationController] Scrolling to section:', sectionId);

            // If navigating to Step 1 — reset color selection to defaultActiveColorId (client requirements)
            if (sectionId === 'colourQuantity') {
                this.colorSectionResetTrigger++
            }

            // Ensure section is unlocked and open
            this.imprintStore.unlockSection(sectionId);
            if (!this.isSectionOpen(sectionId)) {
                this.imprintStore.toggleSectionOpen(sectionId);
            }

            this.$nextTick(() => {
                setTimeout(() => {
                    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
                    if (section) {
                        const currentScroll = window.pageYOffset;
                        const rect = section.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80;

                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);

                        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
                    }
                }, 300);
            });
        },

        handleGoBack() {
            console.log('[ImprintNavigationController] Go back from Review clicked');

            // Determine which section to go back to
            let previousSectionId = 'addons';

            // If Add-Ons section is hidden, go back to Customize
            if (!this.debugConfig.showAddOnsSection) {
                previousSectionId = 'imprintSelection';
                console.log('[ImprintNavigationController] Add-Ons hidden, going back to Customize');
            }

            // Close Review section
            if (this.isSectionOpen('review')) {
                this.imprintStore.toggleSectionOpen('review');
            }

            // Open previous section
            if (!this.isSectionOpen(previousSectionId)) {
                this.imprintStore.toggleSectionOpen(previousSectionId);
            }

            // Scroll to previous section with improved safety logic
            this.$nextTick(() => {
                setTimeout(() => {
                    const section = document.querySelector(`[data-section-id="${previousSectionId}"]`);
                    if (section) {
                        const currentScroll = window.pageYOffset;
                        const rect = section.getBoundingClientRect();
                        const sectionTop = currentScroll + rect.top;
                        const offset = 80;

                        let scrollToPosition = sectionTop - offset;
                        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                        scrollToPosition = Math.min(scrollToPosition, maxScroll);
                        scrollToPosition = Math.max(0, scrollToPosition);

                        console.log('[ImprintNavigationController] Scrolling back to section:', {
                            sectionId: previousSectionId,
                            currentScroll,
                            scrollToPosition
                        });

                        window.scrollTo({
                            top: scrollToPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        console.warn('[ImprintNavigationController] Previous section not found:', previousSectionId);
                    }
                }, 500);
            });
        },

        getSectionErrorCount(sectionId) {
            return this.sectionValidation.getSectionErrorCount(sectionId);
        },
        
        getSectionErrors(sectionId) {
            return this.sectionValidation.getSectionErrors(sectionId);
        },

        /**
         * Whether errors should be shown for a given section.
         * Errors are shown if:
         * 1) The user clicked "Next Step" in that section, OR
         * 2) The Review section was opened (showAllSectionErrors = true)
         */
        isSectionValidated(sectionId) {
            return this.showAllSectionErrors || this.validatedSections.has(sectionId);
        },

        /**
         * Mark a section as validated (user clicked Next Step)
         */
        markSectionValidated(sectionId) {
            this.validatedSections.add(sectionId);
        },

        async handleAddToCart() {
            console.log('[ImprintNavigationController] handleAddToCart triggered');
            
            try {
                // Pass clearAllData as success callback so it's only called after cart is updated.
                // addToCart() catches all internal errors and returns normally (no throw),
                // so we cannot call clearAllData() here - it would run even on failure.
                const { clearAllData } = useDataCleanup();
                await this.addToCartStore.addToCart({ onAfterSuccess: clearAllData });
                
            } catch (error) {
                console.error('[ImprintNavigationController] Error adding to cart:', error);
                // ✅ Do not block tests with alert()
                // TODO: replace with inline error UI / toast component
                console.warn('There was an issue adding the product to cart. Please check your selections and try again.');
            }
        }
    },

    watch: {
        // ✅ Watch for route changes to different products (using productId param)
        '$route.params.productId': {
            handler(newProductId, oldProductId) {
                if (oldProductId && newProductId && newProductId !== oldProductId) {
                    console.log('[ROUTE_CHANGE] Product ID changed:', oldProductId, '→', newProductId);
                    console.log('[ROUTE_CHANGE] Clearing all data from previous product...');
                    
                    const { fullCleanup } = useDataCleanup();
                    const cleaned = fullCleanup();
                    
                    if (cleaned) {
                        console.log('[ROUTE_CHANGE] ✅ Data cleared and stores reset');
                    } else {
                        console.warn('[ROUTE_CHANGE] ⚠️ Cleanup may have failed');
                    }
                }
            }
        },
        
        // ✅ FALLBACK: Watch for pathname changes (for Shopify URLs without route.params)
        '$route.path': {
            handler(newPath, oldPath) {
                // Skip if paths are the same
                if (!oldPath || newPath === oldPath) return;
                
                // Extract product handle from path (e.g., /products/gildan-t-shirt)
                const extractHandle = (path) => {
                    const match = path.match(/\/products\/([^\/\?#]+)/);
                    return match ? match[1] : null;
                };
                
                const oldHandle = extractHandle(oldPath);
                const newHandle = extractHandle(newPath);
                
                // Only cleanup if product handle changed
                if (oldHandle && newHandle && oldHandle !== newHandle) {
                    console.log('[ROUTE_CHANGE] Product path changed:', oldHandle, '→', newHandle);
                    console.log('[ROUTE_CHANGE] Clearing all data from previous product...');
                    
                    const { fullCleanup } = useDataCleanup();
                    const cleaned = fullCleanup();
                    
                    if (cleaned) {
                        console.log('[ROUTE_CHANGE] ✅ Data cleared and stores reset (path watcher)');
                    } else {
                        console.warn('[ROUTE_CHANGE] ⚠️ Cleanup may have failed (path watcher)');
                    }
                }
            }
        }
    },

    async mounted() {
        console.log('[ImprintNavigationController] Starting initialization...');
        
        // Initialize app store first (this loads all product data, application type, etc.)
        try {
            await this.appStore.initialize();
            console.log('[ImprintNavigationController] App store initialized successfully');
            console.log('[ImprintNavigationController] App store state:', {
                mainProductID: this.appStore.mainProductID,
                applicationType: this.appStore.applicationType,
                isCustomBundlePage: this.appStore.isCustomBundlePage,
                isEnvelopProduct: this.appStore.isEnvelopProduct,
                productType: this.appStore.productType,
                isComda: this.appStore.isComda,
                storeID: this.appStore.storeID,
                isCalendarSingle: this.appStore.isCalendarSingle,
                isCalendarMode: this.appStore.isCalendarMode
            });
            
        } catch (error) {
            console.error('[ImprintNavigationController] Failed to initialize app store:', error);
        }
        
        // Load calendar store if in calendar mode
        if (this.appStore.isCalendarMode) {
            console.log('[ImprintNavigationController] Calendar mode detected, loading calendar store...');
            try {
                await this.calendarStore.loadFromLocalStorage();
                console.log('[ImprintNavigationController] Calendar store loaded:', {
                    productsCount: this.calendarStore.products.length,
                    totalQuantity: this.calendarStore.totalQuantity,
                    isValid: this.calendarStore.isValid
                });
            } catch (error) {
                console.error('[ImprintNavigationController] Failed to load calendar store:', error);
            }
        }
        
        // CRITICAL: Load fullJson BEFORE initializing sections
        // This ensures availableMethods and locations are populated when components mount
        this.initFullJson();

        // Load additional location fees from metafield DOM element
        this.imprintStore.initAdditionalLocationFees();
        console.log('[ImprintNavigationController] fullJson loaded:', !!this.imprintStore.fullJson);
        
        // Initialize sections based on application type
        // This must happen AFTER fullJson is loaded
        this.imprintStore.initializeSections(this.appStore.applicationType);
        console.log('[ImprintNavigationController] Sections initialized for:', this.appStore.applicationType);
        console.log('[ImprintNavigationController] Sections loaded:', this.sections);
        console.log('[ImprintNavigationController] First section isOpen:', this.sections[0]?.isOpen);
    },
};
</script>
<style scoped lang="scss">
@use '@/styles/components/_imprint_navigation_controller';
</style>

<style  lang="scss">
@use '@/styles/components/_media_queries';
</style>


